import { City, ReportLogsResponse, State, University, UniversityBranch, UniversityCompany } from "../Models";
import { APIURL } from "./config";

export async function GetCities(token: string) {
  const res = await fetch(`${APIURL}/MasterData/Cities`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const cities: City[] = await res.json();
  return cities;
}

export async function GetUniversities(token: string) {
  const res = await fetch(`${APIURL}/MasterData/ConfirmedUniversities?pageNumber=1&pageSize=1000&search=&urlSearch=`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data: University[] = await res.json();
  return data || [];
}

export async function GetCompanies(token: string) {
  const res = await fetch(`${APIURL}/MasterData/ConfirmedCompanies?pageNumber=1&pageSize=1000&search=&urlSearch=`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const data = await res.json();
  return data || [];
}

// Lazy loading function for universities filter
export async function getUniversitiesForFilter(token: string): Promise<University[]> {
  try {
    const res = await fetch(
      `${APIURL}/MasterData/ConfirmedUniversities?pageNumber=1&pageSize=10000&search=&urlSearch=`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!res.ok) {
      throw new Error('Failed to fetch universities');
    }
    const data: University[] = await res.json();
    return data || [];
  } catch (error) {
    console.error('Error fetching universities for filter:', error);
    return [];
  }
}

// Dynamic search function for companies filter
export async function searchCompanies(
  searchTerm: string,
  token: string,
  limit: number = 50
): Promise<UniversityCompany[]> {
  try {
    const res = await fetch(
      `${APIURL}/MasterData/ConfirmedCompanies?pageNumber=1&pageSize=${limit}&search=${encodeURIComponent(searchTerm)}&urlSearch=`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!res.ok) {
      throw new Error('Failed to search companies');
    }
    const data: UniversityCompany[] = await res.json();
    return data || [];
  } catch (error) {
    console.error('Error searching companies:', error);
    return [];
  }
}

export async function GetMajorCities(token: string) {
  const res = await fetch(`${APIURL}/MasterData/Cities/majorCities`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const cities: City[] = await res.json();
  return cities;
}

export async function GetallSubCities(token: string) {
  const res = await fetch(`${APIURL}/MasterData/Cities/subCities`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const cities: City[] = await res.json();
  return cities;
}

export const fetchConfirmedUniversities = async (
  search = "",
  pageNumber = 1,
  pageSize = 15,
  urlSearch = "",
  token: string = ""
) => {
  const res = await fetch(
    `${APIURL}/MasterData/ConfirmedUniversities?pageNumber=${pageNumber}&pageSize=${pageSize}&search=${search}&urlSearch=${urlSearch}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const data = await res.json();
  return data;
};

export const companyUpdateAI = async (Id: string, token: string) => {
  const response = await fetch(`${APIURL}/MasterData/Company`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(Id),
  });
  try {
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      throw new Error();
    }
  } catch (error) {
    console.error("Error:", error);
  }
};

export const universityUpdateAI = async (Id: string, token: string) => {
  const response = await fetch(`${APIURL}/MasterData/University`, {
    method: "PUT",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(Id),
  });
  try {
    if (response.ok) {
      const data = await response.json();
      return data;
    } else {
      throw new Error("Failed to Update University.");
    }
  } catch (error) {
    console.error("Error:", error);
  }
};

export async function GetStates(token: string) {
  const res = await fetch(`${APIURL}/MasterData/States`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const cities: State[] = await res.json();
  return cities;
}

export const fetchSubCitiesOfMajor = async (majorCityId: string, token: string) => {
  const res = await fetch(`${APIURL}/MasterData/${majorCityId}/subcities`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = res.json();
  return json;
};

export const checkIfUnisExists = async (univName: string, cityNames: string[], url: string | null, token: string) => {
  const res = await fetch(`${APIURL}/MasterData/CheckUnisExists/${univName}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(cityNames),
  });

  if (!res.ok) {
    const errorResponse = await res.json().catch(() => ({}));
    throw { status: res.status, ...errorResponse };
  }

  return res.json();
};

export const checkIfCompExists = async (compName: string, cityNames: string[], url: string | null, token: string) => {
  const res = await fetch(`${APIURL}/MasterData/CheckCompanyExists/${compName}&URL=${url}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(cityNames),
  });

  if (!res.ok) {
    const errorResponse = await res.json().catch(() => ({}));
    throw { status: res.status, ...errorResponse };
  }

  return res.json();
};

export const GetAllUnisByName = async (universityName: string, token: string) => {
  const res = await fetch(`${APIURL}/MasterData/GetAllUnisByName?universityName=${universityName}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = res.json();
  return json;
}

export const GetAllCompsByName = async (compName: string, token: string) => {
  const res = await fetch(`${APIURL}/MasterData/GetAllCompaniesBySearchName?searchName=${compName}`, {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  const json = res.json();
  return json;
}

export const AddUniversities = async (universities: University[], token: string) => {
  const res = await fetch(`${APIURL}/MasterData/AddUniversities`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(universities),
  });
  const json = res.json();
  return json;
}

export const removeEducation = async (candidateId: string, educationIds: string[], token: string) => {
  await fetch(`${APIURL}/MasterData/Education?Candidateid=${candidateId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(educationIds),
  });
}

export const AddEducations = async (candidateId: string, educations: any, token: string) => {
  await fetch(`${APIURL}/MasterData/AddEducation/${candidateId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(educations),
  });
}
export const removeExperience = async (candidateId: string, experienceIds: string[], token: string) => {
  await fetch(`${APIURL}/MasterData/Experience?Candidateid=${candidateId}`, {
    method: "DELETE",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(experienceIds),
  });
}

export const addCompanies = async (companies: UniversityCompany[], token: string) => {
  await fetch(`${APIURL}/MasterData/AddCompanies`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(companies),
  });
}

export const addExperience = async (companies: any, candidateId: string, token: string) => {
  await fetch(`${APIURL}/MasterData/AddExperience/${candidateId}`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(companies),
  });
}

export async function getRoleAssessmentMap(qId: string, token: string) {
  try {

    const response = await fetch(
      `${APIURL}/MasterData/getRolesAndAssessmentBelongTo/${qId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const exists = await response.json();
    return exists;
  } catch (error) {
    console.error(
      "There was a problem while checking the Role Assessment Mapping data:",
      error,
    );
    return null;
  }
}

export async function getQuestionAnalysis(qId: string, token: string, roleId: string = "00000000-0000-0000-0000-000000000000", assessmentId: string = "00000000-0000-0000-0000-000000000000") {
  try {

    const response = await fetch(
      `${APIURL}/MasterData/getQuestionAnalysis/${qId}?roleId=${roleId}&assessmentId=${assessmentId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(
      "There was a problem while checking the Role Assessment Mapping data:",
      error,
    );
    return null;
  }
}
export async function getUniversityBranches(token: string) {
  try {

    const response = await fetch(
      `${APIURL}/MasterData/universityBranches`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(
      "There was a problem while checking the Role Assessment Mapping data:",
      error,
    );
    return null;
  }
}

export async function getUniversityBranchesForRole(roleId: string, token: string) {
  try {
    const response = await fetch(
      `${APIURL}/MasterData/universityBranches/${roleId}`,
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    if (!response.ok) {
      throw new Error("Network response was not ok");
    }
    const data: UniversityBranch[] = await response.json();
    return data;
  } catch (error) {
    console.error(
      "There was a problem while checking the Role Assessment Mapping data:",
      error,
    );
    return null;
  }
}

export const GetConfirmedUnis = async (
  search = "",
  pageNumber = 1,
  pageSize = 15,
  urlSearch = "",
  token: string = ""
) => {
  const res = await fetch(
    `${APIURL}/MasterData/ConfirmedUniversities?pageNumber=${pageNumber}&pageSize=${pageSize}&search=${search}&urlSearch=${urlSearch}`,
    {
      headers: {
        Authorization: `Bearer ${token}`,
      },
    }
  );
  const json = res.json();
  return json;
};


export async function fetchReportLogs(
  token: string,
  pageNumber: number = 1,
  pageSize: number = 20,
  search: string = "",
  startDate: string = "",
  endDate: string = "",
  sortBy: "timestamp" | "email" | "title" = "timestamp",
  sortOrder: "asc" | "desc" = "desc",
  
  // NEW PARAMETERS - Arrays just like RoleLeftSideBar
  status: string[] = ["all"],
  categories: string[] = ["all"],
  roles: string[] = ["all"]
): Promise<ReportLogsResponse | null> {
  try {
    const params = new URLSearchParams({
      pageNumber: pageNumber.toString(),
      pageSize: pageSize.toString(),
      sortBy,
      sortOrder
    });

    if (search) params.append("search", search);
    if (startDate) params.append("startDate", startDate);
    if (endDate) params.append("endDate", endDate);
    
    // NEW FILTER PARAMS - Handle arrays
    if (status && status.length > 0 && !status.includes("all")) {
      status.forEach(s => params.append("status", s.toLowerCase()));
    }
    
    if (categories && categories.length > 0 && !categories.includes("all")) {
      categories.forEach(cat => params.append("categories", cat));
    }
    
    if (roles && roles.length > 0 && !roles.includes("all")) {
      roles.forEach(role => params.append("roles", role));
    }

    const response = await fetch(`${APIURL}/MasterData/ReportLogs?${params.toString()}`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error("Error fetching report logs:", error);
    return null;
  }
}

// Add these helper functions
export const fetchReportLogCategories = async (token: string): Promise<string[]> => {
  try {
    const res = await fetch(`${APIURL}/MasterData/ReportLogs/Categories`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error("Failed to fetch categories");
    return await res.json();
  } catch (error) {
    console.error("Error fetching categories:", error);
    return [];
  }
};

export const fetchReportLogRoles = async (token: string): Promise<string[]> => {
  try {
    const res = await fetch(`${APIURL}/MasterData/ReportLogs/Roles`, {
      method: "GET",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`,
      },
    });

    if (!res.ok) throw new Error("Failed to fetch roles");
    return await res.json();
  } catch (error) {
    console.error("Error fetching roles:", error);
    return [];
  }
};

export const deleteReportLog = async (token: string, id: string) => {
  const res = await fetch(`${APIURL}/MasterData/ReportLogs/${id}`, {
    method: "DELETE",
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to delete report log");
  }
};

export const updateReportLogStatus = async (
  token: string,
  id: string,
  status: "open" | "closed"
) => {
  const res = await fetch(`${APIURL}/MasterData/ReportLogs/${id}/Status`, {
    method: "PATCH",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token}`,
    },
    body: JSON.stringify(status),
  });

  if (!res.ok) {
    const errorText = await res.text();
    throw new Error(errorText || "Failed to update report log status");
  }

  return res.json();
};

export const getAllRoleQuestions = async (
  token: string,
  searchQuery = "",
) => {
  let url = `${APIURL}/AssessmentQuestion/GetRoleQuestions`;

  const params = new URLSearchParams();
  if (searchQuery && searchQuery.trim() !== "") {
    params.append("searchQuery", searchQuery.trim());
  }

  const fetchUrl = params.toString() ? `${url}?${params.toString()}` : url;

  const res = await fetch(fetchUrl, {
    method: "GET",
    headers: {
      Accept: "application/json",
      Authorization: `Bearer ${token}`,
    },
  });

  if (!res.ok) {
    const text = await res.text().catch(() => null);
    throw new Error(text || `Request failed with status ${res.status}`);
  }

  const json = await res.json();
  return json;
};
